import { IconFileTypePdf, IconX } from "@tabler/icons-react";
import { toast, type Id } from "react-toastify";

type SendPdfToStorydocToastOptions = {
  cancelAriaLabel?: string;
  msg?: string;
  onCancel?: () => void;
};

// Per Figma 1:21356 — replaces react-toastify's --info purple variant with a
// near-white card: 1px tinted-purple border, subtle shadow, dark `#12033E`
// foreground, PDF icon on the left, X close button on the right.
const TOAST_CONTAINER_CLASS_NAME =
  "!tw-bg-white !tw-rounded-lg !tw-border tw-border-solid !tw-border-blue/5 !tw-shadow-[0_0_1px_rgba(0,0,0,0.09),0_1px_1px_rgba(0,0,0,0.05),0_2px_1px_rgba(0,0,0,0.01)] !tw-py-3.5 !tw-px-4 !tw-min-h-0";
const TOAST_BODY_CLASS_NAME = "!tw-p-0 !tw-m-0";

const DEFAULT_MESSAGE =
  "We're turning your PDF into a Storydoc. Feel free to keep working — we'll let you know when it's ready.";

// Shared content renderer so the icon stays put when the polling loop swaps
// the message via `toast.update({ render: ... })` — passing a plain string
// would replace the icon+text JSX with bare text and drop the icon.
export const renderPdfToStorydocToastContent = (msg: string) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <IconFileTypePdf
      aria-hidden
      className="tw-size-4 tw-shrink-0 tw-text-blue"
    />
    <span className="tw-font-medium tw-text-sm tw-leading-5 tw-text-blue">
      {msg}
    </span>
  </div>
);

export const sendPdfToStorydocToast = ({
  cancelAriaLabel = "Cancel PDF to Storydoc",
  msg = DEFAULT_MESSAGE,
  onCancel,
}: SendPdfToStorydocToastOptions = {}) => {
  return toast(renderPdfToStorydocToastContent(msg), {
    icon: false,
    autoClose: false,
    closeOnClick: false,
    className: TOAST_CONTAINER_CLASS_NAME,
    bodyClassName: TOAST_BODY_CLASS_NAME,
    closeButton: onCancel
      ? ({ closeToast }) => (
          <button
            type="button"
            aria-label={cancelAriaLabel}
            className="tw-ml-3 tw-flex tw-shrink-0 tw-self-center tw-text-blue tw-transition-opacity hover:tw-opacity-70"
            onClick={(event) => {
              closeToast(event);
              onCancel();
            }}
          >
            <IconX className="tw-size-4" />
          </button>
        )
      : undefined,
  });
};

// Update the running PDF-to-Storydoc toast's message while preserving the
// PDF icon and styling. Use this from the polling loop instead of calling
// `toast.update` with a plain string.
export const updatePdfToStorydocToast = (toastId: Id, msg: string) => {
  toast.update(toastId, {
    render: renderPdfToStorydocToastContent(msg),
  });
};
